import { Fragment, type ReactNode } from 'react'

/**
 * The shared primitives, in the shapes the design boards set.
 *
 * Three of these have no Broadsheet equivalent and exist because a
 * coordinate-bearing archive needs them: the content-type badge and chip, the
 * confidence indicator, and the physical-address block. Everything else is
 * Broadsheet's component layer with this product's density.
 *
 * Sections are separated by whitespace, not by rules or boxes. `Panel` is the
 * one boxed component and is reserved for genuinely discrete items.
 */

/** Section heading: a mono ordinal, a tracked cap label, a hairline to the edge. */
export function SectionHead({
  ordinal,
  title,
  note,
}: {
  ordinal?: string
  title: string
  note?: ReactNode
}) {
  return (
    <div className="mb-3 flex items-baseline gap-3">
      {ordinal && <div className="font-mono text-[10px] tracking-[0.12em] text-accent">{ordinal}</div>}
      <h2 className="chrome">{title}</h2>
      <div className="h-px flex-1 bg-n300" />
      {note && <div className="font-mono text-[10.5px] text-ink2">{note}</div>}
    </div>
  )
}

export function PageHeader({
  title,
  description,
  actions,
  meta,
}: {
  title: string
  description?: string
  actions?: ReactNode
  meta?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4 border-b-2 border-ink pb-3.5">
      <div>
        {meta && <div className="ident mb-1.5 uppercase tracking-[0.12em]">{meta}</div>}
        <h1 className="text-[32px] leading-[1.04]">{title}</h1>
        {description && (
          <p className="mt-2 max-w-[70ch] text-[15.5px] leading-normal text-ink2">{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`panel overflow-hidden ${className}`}>{children}</section>
}

/**
 * An empty state names a first step. Never a shrug, never "0 results" where
 * "nothing here yet" is the truth.
 */
export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-5 text-[15px] leading-normal text-ink2">{children}</p>
}

/**
 * An error names what failed and what survived. Staff see the traceback;
 * readers see the stage.
 */
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof Error ? error.message : String(error)
  return (
    <p role="alert" className="doubt-block mb-3 rounded-r text-[14.5px] leading-normal">
      {message}
    </p>
  )
}

// --- content types ---------------------------------------------------------

/**
 * The six content types. Four hues for the types a researcher actually cites,
 * and neutral for the two structural ones nobody searches for.
 *
 * Colour is never the only cue: each type also carries a three-letter mono
 * code, so the overlay survives colour blindness, a seminar projector and
 * greyscale print.
 */
export const TYPES = {
  article: { code: 'ART', name: 'Article', colour: 'var(--jl-art)', muted: false },
  photograph: { code: 'PHO', name: 'Photograph', colour: 'var(--jl-pho)', muted: false },
  advertisement: { code: 'ADV', name: 'Advertisement', colour: 'var(--jl-adv)', muted: false },
  notice: { code: 'NOT', name: 'Notice', colour: 'var(--jl-not)', muted: false },
  listing: { code: 'LST', name: 'Listing', colour: 'var(--jl-lst)', muted: true },
  furniture: { code: 'FUR', name: 'Furniture', colour: 'var(--jl-fur)', muted: true },
} as const

export type TypeKey = keyof typeof TYPES

/**
 * The pipeline's block vocabulary is finer than these six, because extraction
 * needs to tell a headline from a byline and presentation does not. The six
 * types are a grouping over it, and this is the only place that grouping is
 * defined.
 *
 * A block type absent from this map is a new one the pipeline learned to emit,
 * and it falls back to article rather than disappearing from the overlay: an
 * unlabelled region on the page is worse than a mislabelled one, because a
 * reader can see and question a wrong label.
 */
const BLOCK_TO_TYPE: Record<string, TypeKey> = {
  headline: 'article',
  deck: 'article',
  subhead: 'article',
  byline: 'article',
  credit: 'article',
  body: 'article',
  pull_quote: 'article',
  kicker: 'article',
  article: 'article',

  image: 'photograph',
  caption: 'photograph',
  photograph: 'photograph',

  advertisement: 'advertisement',
  notice: 'notice',

  listing: 'listing',
  table: 'listing',

  masthead: 'furniture',
  folio: 'furniture',
  rule: 'furniture',
  unknown: 'furniture',
}

export function typeOf(key: string | null | undefined) {
  if (!key) return TYPES.article
  if (key in TYPES) return TYPES[key as TypeKey]
  return TYPES[BLOCK_TO_TYPE[key] ?? 'article']
}

/** The hue a region outline takes. Structural types are drawn dashed. */
export function regionStyle(
  blockType: string | null | undefined,
  state: 'rest' | 'hover' | 'selected',
): React.CSSProperties {
  const t = typeOf(blockType)
  if (state === 'selected') {
    // The accent, its 16% wash, and an inner paper hairline that lifts the
    // selection off the print. The wash never exceeds 16%: above that,
    // newsprint stops being readable through it.
    return {
      border: '2px solid var(--jl-accent)',
      // Mixed from the accent token so the wash follows the theme: the dark
      // accent is a lighter maroon, and a literal maroon under it mismatches.
      background: 'color-mix(in srgb, var(--jl-accent) 16%, transparent)',
      outline: '1px solid color-mix(in srgb, var(--color-bg) 85%, transparent)',
      outlineOffset: '-4px',
    }
  }
  if (t.muted) {
    // Structural regions are present and clickable but visually last.
    return { border: `1px dashed ${t.colour}`, background: 'transparent' }
  }
  if (state === 'hover') {
    return { border: `1.5px solid ${t.colour}`, background: 'color-mix(in srgb, var(--color-text) 7%, transparent)' }
  }
  return { border: `1.5px solid ${t.colour}`, background: 'transparent' }
}

/** The solid three-letter tab. Used on region overlays and result rows. */
export function TypeBadge({ type, title }: { type: string | null | undefined; title?: string }) {
  const t = typeOf(type)
  return (
    <span className="badge" style={{ background: t.colour }} title={title ?? t.name}>
      {t.code}
    </span>
  )
}

/** The outlined chip with a count. Used in the overlay filter row. */
export function TypeChip({
  type,
  count,
  active = true,
  onClick,
}: {
  type: string
  count: number
  active?: boolean
  onClick?: () => void
}) {
  const t = typeOf(type)
  const on = count > 0 && active
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className="tag font-mono"
      style={{
        borderColor: on ? t.colour : 'var(--color-neutral-300)',
        color: on ? t.colour : 'var(--color-neutral-500)',
      }}
      title={`${t.name}: ${count}`}
    >
      <span
        className="block h-[7px] w-[7px] rounded-[1px]"
        style={{
          background: on ? t.colour : 'transparent',
          border: on ? 'none' : '1px solid var(--color-neutral-400)',
        }}
      />
      <span className="text-[10.5px] tracking-[0.04em]">{t.code}</span>
      <span className="text-[10px] text-ink2">{count}</span>
    </Tag>
  )
}

// --- doubt -----------------------------------------------------------------

/**
 * Confidence, wherever a number appears.
 *
 * The bar is never the only cue. The figure is always beside it, and below
 * 0.85 the figure turns magenta. A researcher deciding whether to quote
 * something needs a number, not a colour.
 */
export const LOW_CONFIDENCE = 0.85

export function Confidence({
  value,
  width = 52,
  engine,
}: {
  value: number | null | undefined
  width?: number
  engine?: string | null
}) {
  if (value === null || value === undefined) return null
  const low = value < LOW_CONFIDENCE
  const pct = Math.max(0, Math.min(1, value)) * 100
  return (
    <span
      className="flex items-center gap-2"
      title={`Recognition confidence of the text${engine ? ` (${engine})` : ''}: how well it was read from the page, not how well it matches a search.`}
    >
      {engine && <span className="ident">{engine}</span>}
      <span
        className="h-[5px] overflow-hidden rounded-[1px] bg-n300"
        style={{ width }}
        aria-hidden="true"
      >
        <span
          className="block h-full"
          style={{
            width: `${pct}%`,
            background: low ? 'var(--color-accent-2-700)' : 'var(--jl-art)',
          }}
        />
      </span>
      <span className={`font-mono text-[11px] ${low ? 'text-mark' : 'text-ink'}`}>
        {pct.toFixed(1)}%
      </span>
    </span>
  )
}

/**
 * A passage the recognition doubts. The tint marks the block; the caller marks
 * individual tokens with `.doubt-token`. Neither changes the letterforms, so a
 * researcher reading for a quotation sees exactly what the recognition
 * produced.
 */
export function DoubtBlock({
  confidence,
  children,
  className = '',
}: {
  confidence: number | null | undefined
  children: ReactNode
  className?: string
}) {
  const low = confidence !== null && confidence !== undefined && confidence < LOW_CONFIDENCE
  return <div className={`${low ? 'doubt-block' : ''} ${className}`}>{children}</div>
}

/**
 * Anything a model produced, never set in the reading serif. That face is
 * reserved for what the paper printed, and a reader mistaking generated text
 * for something the paper printed is the one failure that would discredit the
 * archive.
 */
export function MachineText({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="machine">
      <div className="machine-label">{label}</div>
      <div>{children}</div>
    </div>
  )
}

// --- the physical address --------------------------------------------------

/**
 * The block that makes the product's claim visible: every item has a physical
 * address on a physical page. Set entirely in the mono, in two spaces at once,
 * because the pixel space is what the interface addresses and the point space
 * is what was printed.
 */
export function AddressBlock({
  rows,
  iiif,
  onCopy,
}: {
  rows: [string, string][]
  iiif?: string | null
  onCopy?: () => void
}) {
  return (
    <div className="bg-surface px-4 py-3">
      <div className="mb-2.5 flex items-baseline justify-between">
        <div className="chrome">Physical address</div>
        {onCopy && (
          <button type="button" className="btn-ghost font-mono text-[10px] tracking-[0.06em]" onClick={onCopy}>
            COPY BBOX
          </button>
        )}
      </div>
      <div className="addr grid grid-cols-[auto_1fr_auto_1fr] gap-x-2.5 gap-y-[3px]">
        {rows.map(([label, value]) => (
          <Fragment key={label}>
            <div className="text-ink2">{label}</div>
            <div>{value}</div>
          </Fragment>
        ))}
      </div>
      {iiif && (
        <div className="ident mt-2.5 break-all border-t border-n300 pt-2">{iiif}</div>
      )}
    </div>
  )
}

/** A dense mono key/value grid, for records and metrics. */
export function RecordGrid({ rows, cols = 2 }: { rows: [string, ReactNode][]; cols?: 1 | 2 }) {
  return (
    <div
      className={`addr grid gap-x-3.5 gap-y-1 ${
        cols === 2 ? 'grid-cols-[auto_1fr_auto_1fr]' : 'grid-cols-[auto_1fr]'
      }`}
    >
      {rows.map(([label, value]) => (
        <Fragment key={label}>
          <div className="text-ink2">{label}</div>
          <div>{value}</div>
        </Fragment>
      ))}
    </div>
  )
}

/**
 * The placeholder for a crop that cannot be shown. It always names what
 * belongs there (page, region) and why it is absent, and it never mimics a
 * loading state.
 */
export function CropPlaceholder({
  lines,
  className = '',
}: {
  lines: string[]
  className?: string
}) {
  return (
    <div
      className={`flex items-center justify-center rounded border border-dashed border-rule2 text-center ${className}`}
      style={{
        background:
          'repeating-linear-gradient(135deg,var(--color-surface) 0 6px,var(--color-bg) 6px 12px)',
      }}
    >
      <div className="font-mono text-[9px] leading-snug text-ink2">
        {lines.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </div>
    </div>
  )
}

/**
 * A drawn chevron, used wherever an arrow is needed.
 *
 * The vendored Latin subsets of Source Serif 4 carry U+2191 and U+2193 but not
 * U+2190 or U+2192, so a literal left or right arrow renders as tofu. Drawing
 * it removes the dependency on what a font subset happens to include, and the
 * stroke follows `currentColor` so it inherits whatever it sits in.
 */
export function Chevron({
  direction = 'right',
  size = 12,
}: {
  direction?: 'left' | 'right' | 'up' | 'down'
  size?: number
}) {
  const rotation = { right: 0, down: 90, left: 180, up: 270 }[direction]
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ transform: `rotate(${rotation}deg)`, flex: 'none' }}
    >
      <path
        d="M6 3.5 10.5 8 6 12.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}


// --- controls from the component sheet (board 10) --------------------------

/**
 * The segmented control: Annotated | Clean, Recognised | Corrected, Rate |
 * Count. One segment is on. The accent fills the active segment only when
 * the control is the surface's primary action (the overlay switch); the ink
 * fill is for everything else.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  accent = false,
  size = 'md',
  ariaLabel,
}: {
  value: T
  options: { value: T; label: ReactNode; title?: string; disabled?: boolean }[]
  onChange: (value: T) => void
  accent?: boolean
  size?: 'sm' | 'md'
  ariaLabel?: string
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`inline-flex overflow-hidden rounded-[--radius-md] border border-rule2 ${
        size === 'sm' ? 'text-[12.5px]' : 'text-[13.5px]'
      }`}
    >
      {options.map((option) => {
        const on = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            title={option.title}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={`${size === 'sm' ? 'px-2.5 py-[3px]' : 'px-3.5 py-1.5'} font-semibold leading-tight transition-colors disabled:opacity-45 ${
              on
                ? accent
                  ? 'bg-accent text-paper'
                  : 'bg-ink text-paper'
                : 'bg-transparent text-ink2 hover:text-body'
            }`}
            style={on ? undefined : { color: 'var(--color-neutral-700)' }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** An active filter, removable. The tint is the accent's, because a filter is a choice the reader made. */
export function FilterChip({
  children,
  onRemove,
}: {
  children: ReactNode
  onRemove?: () => void
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[--radius-md] border px-2 py-[2px] text-[12.5px]"
      style={{ background: 'var(--jl-accent-100)', borderColor: 'var(--jl-accent-200)', color: 'var(--jl-accent)' }}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="font-mono text-[11px] leading-none text-accent hover:text-accent-700"
          aria-label="Remove this filter"
        >
          ×
        </button>
      )}
    </span>
  )
}

/** A keyboard key, as the boards print one: mono, boxed, small. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[18px] items-center justify-center rounded-[3px] border border-rule2 bg-surface px-1 font-mono text-[10px] leading-[16px] text-ink2">
      {children}
    </kbd>
  )
}

/**
 * A banner: tinted ground, a 2 to 3px left rule, no border. Informational
 * takes the accent tint, caution the magenta, success the article green,
 * neutral the surface.
 */
export function Banner({
  tone = 'info',
  children,
  className = '',
}: {
  tone?: 'info' | 'caution' | 'success' | 'neutral'
  children: ReactNode
  className?: string
}) {
  const styles: Record<string, React.CSSProperties> = {
    info: { background: 'var(--jl-accent-100)', boxShadow: 'inset 3px 0 0 var(--jl-accent)' },
    caution: {
      background: 'var(--color-accent-2-100)',
      boxShadow: 'inset 3px 0 0 var(--color-accent-2-700)',
    },
    success: {
      background: 'color-mix(in srgb, var(--jl-art) 10%, var(--color-bg))',
      boxShadow: 'inset 3px 0 0 var(--jl-art)',
    },
    neutral: { background: 'var(--color-surface)', boxShadow: 'inset 3px 0 0 var(--color-neutral-500)' },
  }
  return (
    <div
      role={tone === 'caution' ? 'alert' : undefined}
      className={`rounded-r-[--radius-md] px-3.5 py-2.5 text-[14.5px] leading-normal ${className}`}
      style={styles[tone]}
    >
      {children}
    </div>
  )
}

/** A switch. Flags only; never a toggle for a filter. */
export function Toggle({
  on,
  onChange,
  label,
  disabled = false,
}: {
  on: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className="relative inline-flex h-[18px] w-[32px] flex-none items-center rounded-full border transition-colors disabled:opacity-45"
      style={{
        background: on ? 'var(--jl-accent)' : 'var(--color-neutral-300)',
        borderColor: on ? 'var(--jl-accent)' : 'var(--color-neutral-400)',
      }}
    >
      <span
        className="absolute h-[12px] w-[12px] rounded-full bg-paper transition-transform"
        style={{ transform: on ? 'translateX(16px)' : 'translateX(2px)' }}
      />
    </button>
  )
}

/**
 * Chrome caps with a hairline: the label a panel section takes. Sections are
 * separated by whitespace and this label, never by a box.
 */
export function PanelLabel({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <div className="chrome">{children}</div>
      {note && <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink2">{note}</div>}
    </div>
  )
}

/** A number and its label, as the browse header and the usage report set them. */
export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'mark' }) {
  return (
    <div className="min-w-[64px]">
      <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink2">{label}</div>
      <div className={`font-mono text-[13.5px] ${tone === 'mark' ? 'text-mark' : 'text-body'}`}>{value}</div>
    </div>
  )
}

/** The route and engine of a unit, as the result card prints it. */
export function RouteNote({
  route,
  engine,
  confidence,
}: {
  route: string | null | undefined
  engine: string | null | undefined
  confidence: number | null | undefined
}) {
  const low = confidence !== null && confidence !== undefined && confidence < LOW_CONFIDENCE
  return (
    <span className="font-mono text-[10px] text-ink2">
      {route ? `route ${route}` : ''}
      {route && engine ? ' · ' : ''}
      {engine ?? ''}
      {confidence !== null && confidence !== undefined && (
        <>
          {' '}
          <span className={low ? 'text-mark' : 'text-art'}>{(confidence * 100).toFixed(1)}%</span>
        </>
      )}
    </span>
  )
}
